import Utils from '~/scripts/services/utils';
import APIV5 from '~/models/api';
import QueryColumn from './query-column';
import QueryFrom from './query-from';
import QueryRule from './query-rule';
import QueryVariable from './query-variable';
import QueryWhere from './query-where';
import QueryWhereChild from './query-where-child';
import QueryWhereRule from './query-where-rule';
import QueryWhereRules from './query-where-rules';
import QueryWhereChildren from './query-where-children';
import QueryWhereNode from '~/scripts/services/model/query-where-node';

/** @type {'2.0.0' | '3.0.0'} */
export const DEFAULT_VERSION = '2.0.0';

export default class Query {
	/** @type {any} */
	#ruleLinkTo;

	/**
	 * @param {any} [query]
	 */
	constructor(query = null) {
		this.#ruleLinkTo = undefined;

		/** @type {'2.0.0' | '3.0.0' | undefined} */
		this.version = query ? query.version ?? '2.0.0' : DEFAULT_VERSION;

		/** @type {any[]} */
		this.select = query?.select?.map((/** @type {any} */ column) => new QueryColumn(column)) || [];

		/** @type {any[]} */
		this.from = query?.from?.map((/** @type {any} */ from) => new QueryFrom(from)) || [];

		/** @type {QueryWhere | QueryWhereChild | undefined} */
		this.where = undefined;
		if (query?.where) {
			this.where = this.version && this.version >= '3.0.0'
				? new QueryWhereChild(query.where) : new QueryWhere(query.where);
		}
		else {
			this.setDefaultWhere();
		}

		/** @type {string} */
		this.date = query?.date || '_NOW_';

		/** @type {QueryVariable[]} */
		this.variable = query?.variable?.map(
			(/** @type {any} */ variable) => Object.assign(new QueryVariable(), variable),
		) || [];

		/** @type {string | undefined} */
		this.do_process = typeof query?.do_process === 'string' ? query.do_process.trim() : undefined;
	}

	/**
	 * Generate the query object to be exported or saved to back-end
	 *
	 * @returns {any} Returns query
	 */
	export() {
		if (!Array.isArray(this.select)) {
			this.select = [];
		}
		if (!Array.isArray(this.from)) {
			this.from = [];
		}

		const select = this.select.filter(s => s.includeInQuery !== false).map(s => ({
			inherits_extended: s.inherits_extended,
			kid:               s.kid,
			id:                s.id,
			kdata:             s.kdata,
			name:              s.name,
			node:              s.node,
			op:                s.op,
			path:              s.path,
			type:              s.type,
			width:             s.width,
			field:             s.field,
		}));

		const from = this.from.map(f => ({
			inherits_extended: f.inherits_extended,
			name:              f.name,
			node:              f.node,
			type:              f.type,
		}));

		const where = this.where instanceof QueryWhereChild ? this.exportChildrenMap(this.where) : {
			condition: this.where?.condition,
			rules: Array.isArray(this.where?.rules)
				? this.where?.rules.map((/** @type {any} */ rule) => this.exportRulesMap(rule))
				: [],
		};

		return {
			select,
			from,
			where,
			date:       this.date || '_NOW_',
			version:    this.version,
			variable:   Object.keys(this.variable).length > 0 ? this.variable.map(v => ({
				field:          v.field,
				id:             v.id,
				isa:            v.isa,
				isa_linker_kid: v.isa_linker_kid,
				name:           v.name || undefined,
				operator:       v.operator,
				type:           v.type,
			})) : undefined,
			do_process: this.do_process,
		};
	}

	/**
	 * @param {QueryWhereChild|QueryWhereChildren} c
	 * @returns {any} children map
	 */
	exportChildrenMap(c) {
		/** @type {any} */
		let child;

		if (c instanceof QueryWhereChildren) {
			child = {
				condition: c.condition,
				items: c.items.map(c2 => this.exportChildrenMap(c2)),
			};
		}
		else if (c instanceof QueryWhereChild) {
			if (c.node === null) {
				child = null;
			}
			else {
				child = {
					node:   c.node instanceof QueryWhereNode ? c.node.export() : c.node,
					having: c.having,

					/** @type {any} */
					children: undefined,

					/** @type {any} */
					rules: undefined,
				};
				if (c.children) {
					child.children = {
						condition: c.children.condition,
						items:     c.children.items.map(c2 => this.exportChildrenMap(c2)),
					};
				}
				if (c.rules) {
					child.rules = {
						condition: c.rules.condition,
						items:     c.rules.items?.map(rule => this.exportRulesMap(rule)),
					};
				}
			}
		}
		return child;
	}

	/**
	 * @param {QueryWhereRule | QueryWhereRules | QueryRule} rule
	 * @returns {any} rules map
	 */
	exportRulesMap(rule) {
		if (rule instanceof QueryWhereRules) {
			return {
				condition: rule.condition,
				items:     rule.items.map((/** @type {QueryWhereRule | QueryWhereRules} */ r) => this.exportRulesMap(r)),
			};
		}

		return {
			attribut:   rule.attribut,
			condition:  rule.condition,
			field:      rule.field,
			having:     rule.having?.export(),
			label:      rule instanceof QueryRule ? rule.label : undefined,
			lang_kid:   rule.lang_kid,
			linker_kid: rule instanceof QueryRule ? rule.linker_kid : undefined,
			operator:   rule.operator,
			path:       rule instanceof QueryRule ? rule.path : undefined,
			rules:      Array.isArray(rule.rules) ? rule.rules.map((/** @type {any} */ r) => this.exportRulesMap(r)) : null,
			type:       rule instanceof QueryRule ? rule.type : undefined,
			value:      rule.value,
			variable:   rule.variable,
		};
	}

	/**
	 * Recursive search in the "where" structure using a callback condition
	 *
	 * @param {Function} callback Callback condition
	 * @returns {QueryRule | QueryWhereRule | undefined} Returns the "where" element
	 */
	findRule(callback) {
		let returnValue;

		const rulesIter = (/** @type {QueryWhereRule | QueryWhereRules | QueryRule} */ rule) => {
			if (callback(rule)) {
				returnValue = rule;
			}
			else if ((rule instanceof QueryWhereRule || rule instanceof QueryRule) && Array.isArray(rule.rules)) {
				rule.rules.map(element => rulesIter(element));
			}
			return rule;
		};

		const childrenIter = (/** @type {QueryWhereChild | QueryWhereChildren}) */ child) => {
			if (child instanceof QueryWhereChild && child.rules?.items) {
				child.rules.items.map(r => rulesIter(r));
			}

			if (child instanceof QueryWhereChild && child.children?.items) {
				child.children.items.map(c => childrenIter(c));
			}
			return child;
		};

		if (this.version && this.version >= '3.0.0' && this.where instanceof QueryWhereChild) {
			if (this.where?.rules?.items) {
				this.where.rules.items.map((/** @type {any} */ r) => rulesIter(r));
			}
			if (this.where?.children?.items) {
				this.where.children.items.map((/** @type {QueryWhereChild} */ child) => childrenIter(child));
			}
		}
		else if (Array.isArray(this.where?.rules)) {
			this.where?.rules.map((/** @type {any} */ r) => rulesIter(r));
		}

		return returnValue;
	}

	async fixMissingNames() {
		this.from.map(async from => {
			if (from.node && !from.name) {
				const repreList = await APIV5.instance.repreListSimple({
					items: [from.node],
					mode:  'simple',
				}, { id: 'query.fix-names' });
				if (from.node in repreList.result) {
					from.name = repreList.result[from.node];
				}
			}
			return from;
		});
		this.select.map(async select => {
			if (select.kid && !select.name) {
				const repreList = await APIV5.instance.repreListSimple({
					items: [select.kid],
					mode:  'simple',
				}, { id: 'query.fix-names' });
				if (select.kid in repreList.result) {
					select.name = repreList.result[select.kid];
				}
			}
			return select;
		});
	}

	getLinkToValues() {
		return this.#ruleLinkTo?.value;
	}

	/**
	 * Replaces the '_KID_' string in "where" structure by the actual KID
	 *
	 * @param {string} kid The KID to replace
	 */
	replaceWhereKid(kid) {
		const recWhereInRules = (
			/** @type {(QueryRule | QueryWhere)[] | (QueryWhereRules | QueryWhereRule)[]} */ rules,
		) => {
			if (rules) {
				for (const rule of rules) {
					if ((rule instanceof QueryRule || rule instanceof QueryWhere) && rule.rules) {
						recWhereInRules(rule.rules);
					}
					else if (rule instanceof QueryWhereRule && rule.rules) {
						recWhereInRules(rule.rules.items);
					}
					else if (rule instanceof QueryWhereRules) {
						recWhereInRules(rule.items);
					}
					if ((rule instanceof QueryRule || rule instanceof QueryWhereRule) && Array.isArray(rule.value)) {
						for (const [k, v] of rule.value.entries()) {
							if (v.kid === '_KID_' && kid) {
								rule.value[k].kid = kid;
							}
						}
					}
				}
			}
		};

		const recWhereInChildrenItems = (/** @type {QueryWhereChild[]} */ items) => {
			for (const item of items) {
				if (item.rules?.items) {
					recWhereInRules(item.rules.items);
				}
				if (item.children?.items) {
					recWhereInChildrenItems(item.children.items);
				}
			}
		};

		if (this.where instanceof QueryWhereChild && this.where?.children?.items) {
			recWhereInChildrenItems(this.where.children.items);
		}
		if (this.where instanceof QueryWhere) {
			recWhereInRules(this.where?.rules);
		}
	}

	reset() {
		this.select.length = 0;
		this.from.length = 0;
		this.setDefaultWhere();
		this.date = '_NOW_';
		this.variable.length = 0;
	}

	setDefaultWhere() {
		// @ts-ignore
		this.where = this.version >= '3.0.0' ? new QueryWhereChild() : new QueryWhere();
	}

	/**
	 * @param {import('~/scripts/services/model/form').default} form
	 * @param {any} key
	 */
	setRuleFromFormLinkTo(form, key) {
		if (form?.linkTo.length > 0 && this.where instanceof QueryWhere) {
			if (!Array.isArray(this.where.rules)) {
				this.where.rules = [];
			}
			if (this.where.rules.length > 0) {
				this.where.condition = 'OR';
				// if there is more than 1 rule, create a new group and move the conditions in it
				// @ts-ignore
				if (this.where.rules.length > 1 && this.where.condition === 'AND') {
					const rules = [...this.where.rules];
					const { condition } = this.where;
					this.where.rules.length = 0;
					this.where.rules.push(new QueryWhere({
						condition,
						rules,
					}));
				}
			}
			for (const linkTo of form.linkTo.filter(l => l.key === key)) {
				const kid = linkTo.path.at(-1).value;
				const path = [
					Utils.reversePath(linkTo.path).map((/** @type {any} */ p) => ({
						flow:       p.flow,
						kid:        p.kid,
						linker_kid: p.linker_kid,
						name:       p.name,
					}))[0],
				];

				if (kid) {
					if (this.#ruleLinkTo) {
						this.#ruleLinkTo.operator = 'in';
						this.#ruleLinkTo.value.push({ kid });
					}
					else {
						this.#ruleLinkTo = {
							condition:  'AND',
							field:      path[0].kid,
							having:     null,
							id:         path[0].kid,
							label:      path[0].name,
							linker_kid: path[0].linker_kid,
							operator:   'equal_kid',
							path,
							type:       linkTo.type,
							value:      [{ kid }],
						};
						if (Array.isArray(this.where.rules)) {
							this.where.rules.push(this.#ruleLinkTo);
						}
					}
				}
			}
		}
	}
}
